/**
 * @qd/protocol — wire types shared between web and server.
 *
 * Phase 5 surface: lobby-complete, gameplay action union + GameState landed.
 *
 * Conventions:
 * - All messages are JSON-serializable. Server and client never send anything else.
 * - `seq` is a monotonic counter on `LobbyState`/`GameState` snapshots; clients drop
 *   stale frames whose `seq` is lower than the last applied one.
 * - Quiz answer keys NEVER appear in any message. The server stores `correctIndex`
 *   internally and only synthesises a result; see `ClientSafeQuestion`.
 * - All IDs are branded so accidental string mixups fail at compile time.
 */

// -----------------------------------------------------------------------------
// Versioning
// -----------------------------------------------------------------------------

/** Bump when ClientMsg / ServerMsg shapes change in a non-additive way. */
export const PROTOCOL_VERSION = 1 as const;

// -----------------------------------------------------------------------------
// Branded IDs
// -----------------------------------------------------------------------------

declare const sessionIdBrand: unique symbol;
declare const playerIdBrand: unique symbol;
declare const authTokenBrand: unique symbol;

/** Word-coded session identifier, e.g. "CHALK-PENCIL-23". */
export type SessionId = string & { readonly [sessionIdBrand]: true };
/** Server-issued player identifier (UUID). */
export type PlayerId = string & { readonly [playerIdBrand]: true };
/** Opaque per-player auth token. Stored client-side in localStorage. */
export type AuthToken = string & { readonly [authTokenBrand]: true };

/** Helper builders. Cheap casts; brands exist only at the type level. */
export const asSessionId = (s: string): SessionId => s as SessionId;
export const asPlayerId = (s: string): PlayerId => s as PlayerId;
export const asAuthToken = (s: string): AuthToken => s as AuthToken;

// -----------------------------------------------------------------------------
// Domain enums
// -----------------------------------------------------------------------------

/** Six locked player pigments from the design system. */
export type PlayerColorKey =
  | 'prussian'
  | 'vermillion'
  | 'mustard'
  | 'verdigris'
  | 'tyrian'
  | 'bone';

export const PLAYER_COLORS: readonly PlayerColorKey[] = [
  'prussian',
  'vermillion',
  'mustard',
  'verdigris',
  'tyrian',
  'bone',
] as const;

/** Coarse phase machine. Lobby-side phases own this round; gameplay phases
 *  (`draftPlan`, `documentDeploy`, etc.) land in Phase 5. */
export type Phase = 'lobby' | 'playing' | 'ended';

/** Map sizing knob. 2-3p: small (frontend+backend), 4p: medium, 5p: large. */
export type MapSizeKey = 'small' | 'medium' | 'large';

/** Global question difficulty modifier. */
export type DifficultyKey = 'easy' | 'medium' | 'hard';

/** Reasons the server may reject a ClientMsg. */
export type ErrorCode =
  | 'auth_failed'
  | 'invalid_action'
  | 'wrong_phase'
  | 'not_your_turn'
  | 'session_full'
  | 'session_started'
  | 'name_taken'
  | 'color_taken'
  | 'malformed_message'
  | 'rate_limited'
  // Reducer-specific (Phase 5)
  | 'unknown_territory'
  | 'unknown_card'
  | 'not_your_territory'
  | 'not_adjacent'
  | 'insufficient_cubes'
  | 'insufficient_reserve'
  | 'tier_locked'
  | 'card_chain_violation'
  | 'no_active_combat'
  | 'combat_already_active'
  | 'fortify_already_used'
  | 'game_over';

// -----------------------------------------------------------------------------
// Lobby state
// -----------------------------------------------------------------------------

export interface LobbyPlayer {
  readonly id: PlayerId;
  readonly name: string;
  readonly color: PlayerColorKey | null;
  readonly isReady: boolean;
  readonly isAdmin: boolean;
  readonly isConnected: boolean;
  /** Server-side timestamp (ms since epoch) for stable slot ordering. */
  readonly joinedAt: number;
}

export interface SessionSettings {
  readonly mapSize: MapSizeKey;
  readonly difficulty: DifficultyKey;
  /** Per-turn timer in seconds. 0 disables the timer. */
  readonly turnTimer: number;
}

export const DEFAULT_SESSION_SETTINGS: SessionSettings = {
  mapSize: 'medium',
  difficulty: 'medium',
  turnTimer: 90,
} as const;

export interface LobbyState {
  readonly sessionId: SessionId;
  readonly phase: Phase;
  readonly players: readonly LobbyPlayer[];
  readonly settings: SessionSettings;
  readonly hostId: PlayerId | null;
  /** Monotonic broadcast counter; clients drop frames whose seq is lower. */
  readonly seq: number;
  /** Wall clock from the server, useful for timer drift on reconnect. */
  readonly serverTime: number;
}

// -----------------------------------------------------------------------------
// Quiz wire types — answer keys NEVER leave the server
// -----------------------------------------------------------------------------

export interface ClientSafeQuestion {
  readonly id: string;
  readonly tier: 1 | 2 | 3;
  readonly documentType: string;
  readonly kind: 'mcq' | 'tf' | 'scenario';
  readonly difficulty: DifficultyKey;
  readonly prompt: string;
  /** Absent for `tf`; present for `mcq` / `scenario`. */
  readonly choices?: readonly string[];
  readonly isoClauseRef: string;
}

// -----------------------------------------------------------------------------
// Gameplay phases & turn structure (Phase 5)
// -----------------------------------------------------------------------------

/** Per-turn phase machine. The reducer transitions through these in order. */
export type TurnPhase =
  | 'draft-plan'
  | 'document-deploy'
  | 'execute-tests'
  | 'incident-fortify';

/** Static documentation card identifiers. The deck is hand-curated. */
export type DocCardId =
  | 'doc-test-policy'
  | 'doc-test-strategy'
  | 'doc-test-plan'
  | 'doc-env-readiness'
  | 'doc-test-model'
  | 'doc-test-procedure';

/** Static event card identifiers. */
export type EventCardId =
  | 'evt-server-crash'
  | 'evt-compliance-audit'
  | 'evt-flaky-test'
  | 'evt-release-deadline'
  | 'evt-production-incident'
  | 'evt-audit-failure'
  | 'evt-knowledge-loss'
  | 'evt-test-tool-adoption'
  | 'evt-successful-audit'
  | 'evt-data-breach'
  | 'evt-defect-triage-backlog'
  | 'evt-environment-outage'
  | 'evt-acceptance-test-boom'
  | 'evt-regression-win'
  | 'evt-test-charter-shortfall';

/** Question identifier — opaque string from `@qd/questions`. */
export type QuestionId = string;

// -----------------------------------------------------------------------------
// AttackState — combat state machine inside a turn
// -----------------------------------------------------------------------------

export type AttackState =
  | { readonly kind: 'idle' }
  | {
      readonly kind: 'sourceSelected';
      readonly sourceId: string;
      readonly validTargets: readonly string[];
    }
  | {
      readonly kind: 'targetSelected';
      readonly sourceId: string;
      readonly targetId: string;
      readonly committedCubes: number;
    }
  | {
      readonly kind: 'combatActive';
      readonly sourceId: string;
      readonly targetId: string;
      readonly committedCubes: number;
      readonly question: ClientSafeQuestion;
      /** Reducer-supplied step counter so server-side question lookup is deterministic. */
      readonly questionStep: number;
      /**
       * Buzzer-race lockout flags. Either side may dispatch
       * `ANSWER_QUESTION`; a wrong answer applies a -1 cube penalty to
       * the wrong-answerer's territory and locks them out of further
       * submissions for THIS question. The other side still has a
       * chance. If both sides lock out, combat resolves with no
       * territory change (penalties already applied). Initialised to
       * `false / false` on entry from `COMMIT_ATTACK`.
       */
      readonly attackerLockedOut: boolean;
      readonly defenderLockedOut: boolean;
    }
  | {
      readonly kind: 'combatResolved';
      readonly sourceId: string;
      readonly targetId: string;
      readonly committedCubes: number;
      /**
       * Outcome from the attacker's perspective:
       *  - `'hit'`: attacker won the buzzer race (correct first), defender lost a cube.
       *  - `'miss'`: defender won the race OR both locked out — attack ended,
       *    territory unchanged. Cube penalties already applied during combatActive.
       */
      readonly outcome: 'hit' | 'miss';
      /**
       * ISO/IEC/IEEE 29119-3 explanation paragraph for the question that
       * just resolved. Populated from the question registry's `explanation`
       * field at the moment of resolution (both `ANSWER_QUESTION` and
       * `TURN_TIMEOUT` paths). Broadcast to ALL connected clients —
       * attacker AND non-attackers see it, since it's public educational
       * content. Does NOT reveal the correct choice index; the wire-
       * protocol "no answer keys" invariant is preserved.
       *
       * NOTE: the buzzer-race redesign removed `canContinue`. Each
       * attack is a single question; `combatResolved` is a brief reveal
       * frame before transitioning back to `idle` via `END_COMBAT`. There
       * is no chained second question, so no `CONTINUE_ATTACK` action.
       */
      readonly explanation: string;
    }
  | {
      readonly kind: 'territoryConquered';
      readonly sourceId: string;
      readonly targetId: string;
      readonly committedCubes: number;
      /** Player must move at least this many cubes from source to target. */
      readonly mustMoveAtLeast: number;
      /** Maximum movable (= source.cubeCount - 1 at the moment of conquest). */
      readonly mustMoveAtMost: number;
    };

// -----------------------------------------------------------------------------
// DashboardState — per-player ISO/IEC/IEEE 29119-3 dashboard
// -----------------------------------------------------------------------------

export interface DashboardState {
  readonly hasTestPolicy: boolean;
  readonly hasTestPlan: boolean;
  readonly hasEnvReadiness: boolean;
  readonly hasTestModel: boolean;
  readonly testCases: number;
  readonly testProcedures: number;
  /** Highest tier currently buildable (1, 2, or 3). */
  readonly tier: 1 | 2 | 3;
}

// -----------------------------------------------------------------------------
// Runtime territory / module state — dynamic; static config lives in @qd/map
// -----------------------------------------------------------------------------

export interface TerritoryRuntimeState {
  readonly territoryId: string;
  readonly controllingPlayerId: PlayerId | null;
  readonly cubeCount: number;
  /** True iff its controller has placed an Env-Readiness flag here. */
  readonly hasEnvReadiness: boolean;
  /** Reset at start of round; one fortify per territory per round. */
  readonly fortifiedThisRound: boolean;
}

export interface ModuleRuntimeState {
  readonly moduleId: string;
  readonly fullyControlledByPlayerId: PlayerId | null;
}

// -----------------------------------------------------------------------------
// Deck<T> — generic finite deck with seeded shuffle support
// -----------------------------------------------------------------------------

export interface Deck<TId extends string> {
  /** Cards still face-down, in draw order (top of deck = index 0). */
  readonly draw: readonly TId[];
  /** Cards already played/discarded this match. */
  readonly discard: readonly TId[];
}

// -----------------------------------------------------------------------------
// Player runtime state — extends LobbyPlayer with gameplay fields
// -----------------------------------------------------------------------------

export interface Player {
  readonly id: PlayerId;
  readonly name: string;
  readonly color: PlayerColorKey;
  readonly isAdmin: boolean;
  readonly isConnected: boolean;
  readonly reserveCubes: number;
  readonly dashboard: DashboardState;
  /** Documentation cards currently in hand. */
  readonly hand: readonly DocCardId[];
  /** Once eliminated (zero territories), no more turns. */
  readonly eliminated: boolean;
}

// -----------------------------------------------------------------------------
// GameEvent log entries (append-only)
// -----------------------------------------------------------------------------

export type GameEvent =
  | { readonly kind: 'gameStarted'; readonly seed: string; readonly playerOrder: readonly PlayerId[] }
  | {
      readonly kind: 'phaseChanged';
      readonly phase: TurnPhase;
      readonly playerId: PlayerId;
      readonly turnNumber: number;
    }
  | { readonly kind: 'documentationPlayed'; readonly playerId: PlayerId; readonly cardId: DocCardId }
  | {
      readonly kind: 'cubesDeployed';
      readonly playerId: PlayerId;
      readonly territoryId: string;
      readonly count: number;
    }
  | {
      readonly kind: 'attackInitiated';
      readonly playerId: PlayerId;
      readonly sourceId: string;
      readonly targetId: string;
      readonly committedCubes: number;
    }
  | {
      readonly kind: 'questionAnswered';
      readonly playerId: PlayerId;
      readonly questionId: QuestionId;
      readonly correct: boolean;
    }
  | {
      readonly kind: 'territoryConquered';
      readonly attackerId: PlayerId;
      readonly defenderId: PlayerId | null;
      readonly territoryId: string;
    }
  | {
      readonly kind: 'fortifyExecuted';
      readonly playerId: PlayerId;
      readonly fromId: string;
      readonly toId: string;
      readonly count: number;
    }
  | { readonly kind: 'eventCardDrawn'; readonly cardId: EventCardId }
  | { readonly kind: 'releaseDeadlineTriggered' }
  | { readonly kind: 'turnEnded'; readonly playerId: PlayerId; readonly turnNumber: number }
  | { readonly kind: 'playerEliminated'; readonly playerId: PlayerId }
  | { readonly kind: 'gameEnded'; readonly winnerId: PlayerId | null };

// -----------------------------------------------------------------------------
// Learning telemetry — per-question per-player answer log
// -----------------------------------------------------------------------------

/**
 * One player's answer to one question. Carries only the submitted value
 * + the server-resolved correctness flag. The wire-protocol invariant
 * "answer keys never leave the server" holds because `isCorrect` is a
 * boolean derived from `correctIndex` — the key itself is never sent.
 */
export interface LearningAnswer {
  readonly choiceIndex: number | boolean;
  readonly isCorrect: boolean;
}

// -----------------------------------------------------------------------------
// GameState — server-authoritative gameplay snapshot
// -----------------------------------------------------------------------------

export interface GameState {
  readonly sessionId: SessionId;
  /** Coarse phase. Once gameplay starts this is `'playing'`; ends at `'ended'`. */
  readonly outerPhase: 'playing' | 'ended';
  readonly phase: TurnPhase;
  readonly currentPlayerIdx: number;
  readonly turnNumber: number;
  readonly roundNumber: number;
  readonly releaseDeadlineTriggered: boolean;
  readonly winnerId: PlayerId | null;
  readonly playerOrder: readonly PlayerId[];
  readonly players: readonly Player[];
  readonly territories: Readonly<Record<string, TerritoryRuntimeState>>;
  readonly modules: Readonly<Record<string, ModuleRuntimeState>>;
  readonly decks: {
    readonly questions: Deck<QuestionId>;
    readonly events: Deck<EventCardId>;
    readonly documentation: Deck<DocCardId>;
  };
  readonly attackState: AttackState;
  readonly eventLog: readonly GameEvent[];
  /**
   * Per-question learning telemetry: every player's submitted answer +
   * correctness for every question that's been asked this match. The UI
   * uses this to show bystanders their personal feedback at
   * `combatResolved` so weaker players still engage with each question.
   * Keyed by `QuestionId` then `PlayerId`. Combat outcome is owned by
   * the attacker AND defender's `ANSWER_QUESTION` dispatches under the
   * buzzer-race model — entries here are advisory bystander telemetry.
   */
  readonly learningAnswers: Readonly<
    Record<QuestionId, Readonly<Record<PlayerId, LearningAnswer>>>
  >;
  /** PRNG seed; combined with `actionCount` to derive the per-action stream. */
  readonly seed: string;
  /** Total reducer applications since `createInitialState`. Drives PRNG keying. */
  readonly actionCount: number;
  /** Monotonic broadcast counter for clients. */
  readonly seq: number;
  readonly serverTime: number;
}

// -----------------------------------------------------------------------------
// GameAction — full discriminated union (Phase 5)
// -----------------------------------------------------------------------------

/** Combat-stage attacking-cube commitment. Risk-style 1..3. */
export type AttackingCubes = 1 | 2 | 3;

export type GameAction =
  // System / bootstrap
  | { readonly type: 'START_GAME'; readonly seed: string; readonly playerOrder: readonly PlayerId[] }
  // Document & deploy phase
  | { readonly type: 'PLAY_DOCUMENTATION_CARD'; readonly cardId: DocCardId }
  | { readonly type: 'DEPLOY_CUBES'; readonly territoryId: string; readonly count: number }
  // Execute tests phase — combat
  | { readonly type: 'SELECT_SOURCE'; readonly territoryId: string }
  | { readonly type: 'SELECT_TARGET'; readonly territoryId: string }
  | { readonly type: 'COMMIT_ATTACK'; readonly cubes: AttackingCubes }
  /**
   * Buzzer-race answer. Dispatchable by EITHER the attacker (the current
   * turn-holder) OR the defender (the territory's current controller).
   * Bystanders use `SUBMIT_LEARNING_ANSWER` instead — it records personal
   * telemetry without affecting combat. First correct answer resolves
   * combat: attacker correct → defender loses a cube (full conquest if
   * depleted); defender correct → attacker loses a cube and the attack
   * ends. Wrong answers cost the wrong-answerer 1 cube and lock them out
   * of THIS question; if both lock out, combat ends with no territory
   * change. See reducer for full semantics.
   */
  | { readonly type: 'ANSWER_QUESTION'; readonly choiceIndex: number | boolean }
  | { readonly type: 'END_COMBAT' }
  | { readonly type: 'MOVE_CUBES_AFTER_CONQUEST'; readonly count: number }
  /**
   * Bystander-only learning submission. Records the submitted choice +
   * server-derived correctness into
   * `state.learningAnswers[questionId][playerId]`. Does NOT advance the
   * combat state machine — under the buzzer-race model the attacker AND
   * defender both use `ANSWER_QUESTION` to resolve combat; only the
   * remaining (non-attacker, non-defender) bystanders submit through this
   * action for personal telemetry. Rejected if the player is the attacker
   * or the defender, or has already submitted for the same question.
   */
  | {
      readonly type: 'SUBMIT_LEARNING_ANSWER';
      readonly playerId: PlayerId;
      readonly questionId: QuestionId;
      readonly choiceIndex: number | boolean;
    }
  // Incident & fortify phase
  | { readonly type: 'FORTIFY'; readonly fromId: string; readonly toId: string; readonly count: number }
  | { readonly type: 'DRAW_EVENT_CARD' }
  // Phase / turn flow
  | { readonly type: 'END_PHASE' }
  | { readonly type: 'END_TURN' }
  /**
   * Per-turn timer expired with no attacker submission. Resolves the
   * in-flight combat as a miss (matches `ANSWER_QUESTION(-1)`) AND
   * force-ends the current player's turn, advancing to the next player.
   * No-op if `attackState.kind` is anything other than `combatActive`
   * (the attacker has already submitted; a momentary pause on
   * combatResolved / territoryConquered shouldn't punish the turn).
   * Server validates that `playerId` matches the current turn's player
   * and that the elapsed real-time meets the configured timer.
   */
  | { readonly type: 'TURN_TIMEOUT'; readonly playerId: PlayerId };

// -----------------------------------------------------------------------------
// ClientMsg — everything a connected client may send
// -----------------------------------------------------------------------------

export type ClientMsg =
  | JoinSessionMsg
  | LeaveSessionMsg
  | SetNameMsg
  | SetColorMsg
  | ToggleReadyMsg
  | UpdateSettingsMsg
  | KickPlayerMsg
  | BeginMatchMsg
  | GameActionMsg
  | PingMsg;

export interface JoinSessionMsg {
  readonly type: 'joinSession';
  readonly token?: AuthToken;
  readonly name?: string;
  readonly color?: PlayerColorKey;
}

export interface LeaveSessionMsg {
  readonly type: 'leaveSession';
}

export interface SetNameMsg {
  readonly type: 'setName';
  readonly name: string;
}

export interface SetColorMsg {
  readonly type: 'setColor';
  readonly color: PlayerColorKey;
}

export interface ToggleReadyMsg {
  readonly type: 'toggleReady';
}

export interface UpdateSettingsMsg {
  readonly type: 'updateSettings';
  readonly settings: Partial<SessionSettings>;
}

export interface KickPlayerMsg {
  readonly type: 'kickPlayer';
  readonly playerId: PlayerId;
}

export interface BeginMatchMsg {
  readonly type: 'beginMatch';
}

export interface GameActionMsg {
  readonly type: 'gameAction';
  readonly action: GameAction;
}

export interface PingMsg {
  readonly type: 'ping';
  readonly nonce?: number;
}

// -----------------------------------------------------------------------------
// ServerMsg — everything the server may send
// -----------------------------------------------------------------------------

export type ServerMsg = HelloMsg | LobbyStateMsg | GameStateMsg | ErrorMsg | PongMsg;

/** First message after a successful connect / token issue. */
export interface HelloMsg {
  readonly type: 'hello';
  readonly protocolVersion: typeof PROTOCOL_VERSION;
  readonly sessionId: SessionId;
  readonly playerId: PlayerId;
  readonly token: AuthToken;
  readonly serverTime: number;
}

export interface LobbyStateMsg {
  readonly type: 'lobbyState';
  readonly state: LobbyState;
}

/** Server broadcasts a fresh GameState snapshot after every successful gameAction. */
export interface GameStateMsg {
  readonly type: 'gameState';
  readonly state: GameState;
}

export interface ErrorMsg {
  readonly type: 'error';
  readonly code: ErrorCode;
  readonly message: string;
}

export interface PongMsg {
  readonly type: 'pong';
  readonly nonce?: number;
  readonly serverTime: number;
}

// -----------------------------------------------------------------------------
// Type guards (cheap, structural, runtime-safe)
// -----------------------------------------------------------------------------

export function isClientMsg(value: unknown): value is ClientMsg {
  if (typeof value !== 'object' || value === null) return false;
  const t = (value as { type?: unknown }).type;
  if (typeof t !== 'string') return false;
  return CLIENT_MSG_TYPES.has(t);
}

const CLIENT_MSG_TYPES = new Set<string>([
  'joinSession',
  'leaveSession',
  'setName',
  'setColor',
  'toggleReady',
  'updateSettings',
  'kickPlayer',
  'beginMatch',
  'gameAction',
  'ping',
]);

// -----------------------------------------------------------------------------
// Validation helpers (server-side input shaping)
// -----------------------------------------------------------------------------

/** ASCII + Turkish diacritics; 2-16 chars; matches Phase 3 lobby spec. */
export const PLAYER_NAME_REGEX = /^[A-Za-zÇĞİÖŞÜçğıöşü0-9 _-]{2,16}$/;

/** Format a SessionId looks like: `WORD-WORD-NN`. */
export const SESSION_CODE_REGEX = /^[A-Z]+-[A-Z]+-\d{2}$/;
